import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { Download, Heart, Loader2 } from 'lucide-react';
import { useSixMWTTrends } from '../../lib/hooks/useTrends';

interface SixMWDTrendsCardProps {
  patientId: string;
  patientName: string;
}

export function SixMWDTrendsCard({ patientId, patientName }: SixMWDTrendsCardProps) {
  const [timeRange, setTimeRange] = useState('6weeks');
  
  // Get weeks from timeRange
  const getWeeks = (range: string) => {
    if (range === '12weeks') return 12;
    if (range === '6months') return 24;
    return 6;
  };

  // Fetch 6MWT trends from backend
  const { data: trendsResponse, isLoading, isError } = useSixMWTTrends(
    parseInt(patientId),
    getWeeks(timeRange)
  );

  // Transform backend data to chart format
  const sixMWDData = trendsResponse?.data.map((item, index) => ({
    week: index + 1,
    actual: item.distance || 0,
    target: item.target,
    baseline: item.baseline,
    date: item.date,
    change: item.distance ? item.distance - item.baseline : 0
  })) || [];

  const currentData = sixMWDData[sixMWDData.length - 1] || {
    actual: 0,
    target: 450,
    baseline: 0,
    change: 0
  };

  const baseline = trendsResponse?.summary.baseline || currentData.baseline;
  const target = trendsResponse?.summary.target || currentData.target;

  // Loading state
  if (isLoading) {
    return (
      <Card className="shadow-lg bg-white">
        <CardHeader className="bg-gradient-to-r from-[#2762AC]/5 to-[#14CAF4]/5">
          <CardTitle className="text-primary flex items-center gap-2">
            <Heart className="w-5 h-5" />
            6-Minute Walk Distance (6MWD) Trends
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading 6MWD trends...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (isError || !trendsResponse) {
    return (
      <Card className="shadow-lg bg-white">
        <CardHeader className="bg-gradient-to-r from-[#2762AC]/5 to-[#14CAF4]/5">
          <CardTitle className="text-primary flex items-center gap-2">
            <Heart className="w-5 h-5" />
            6-Minute Walk Distance (6MWD) Trends
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">No 6MWD data available</p>
            <p className="text-xs text-muted-foreground mt-1">Patient hasn't completed any 6-minute walk tests yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg bg-white">
      <CardHeader className="bg-gradient-to-r from-[#2762AC]/5 to-[#14CAF4]/5">
        <CardTitle className="text-primary flex items-center gap-2">
          <Heart className="w-5 h-5" />
          6-Minute Walk Distance (6MWD) Trends
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Section - 3 Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Current 6MWD */}
          <div className="p-4 bg-white border-2 border-[#2762AC] rounded-xl shadow-sm">
            <p className="text-xs text-[#494949] mb-1">Current 6MWD</p>
            <p className="text-[#2762AC] text-2xl">{currentData.actual} m</p>
            <p className="text-xs text-[#494949] mt-1">{trendsResponse.summary.total_tests} tests</p>
          </div>

          {/* Target Distance */}
          <div className="p-4 bg-white border-2 border-[#F07D13] rounded-xl shadow-sm">
            <p className="text-xs text-[#494949] mb-1">Target Distance</p>
            <p className="text-[#F07D13] text-2xl">{target} m</p>
            <p className="text-xs text-[#494949] mt-1">Rehabilitation Goal</p>
          </div>

          {/* Progress */}
          <div className="p-4 bg-white border-2 border-[#14CAF4] rounded-xl shadow-sm">
            <p className="text-xs text-[#494949] mb-1">Progress</p>
            <p className={`text-2xl ${currentData.change >= 0 ? 'text-green-500' : 'text-[#F07D13]'}`}>
              {currentData.change > 0 ? '+' : ''}{currentData.change} m
            </p>
            <p className="text-xs text-[#494949] mt-1">vs baseline ({trendsResponse.summary.progress_percentage.toFixed(0)}%)</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Time Range:</span>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6weeks">6 Weeks</SelectItem>
                <SelectItem value="12weeks">12 Weeks</SelectItem>
                <SelectItem value="6months">6 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="border-[#F07D13] text-[#F07D13] hover:bg-[#F07D13]/10">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>

        {/* Trend Line Chart */}
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sixMWDData}>
              <defs>
                <linearGradient id="sixMWDGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2762AC" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#2762AC" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
              <XAxis 
                dataKey="week" 
                stroke="#494949"
                tick={{ fontSize: 12 }}
                label={{ value: 'Week', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
              />
              <YAxis 
                stroke="#494949"
                domain={['auto', 'auto']}
                tick={{ fontSize: 12 }}
                label={{ value: 'Distance (m)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E5E5E3',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '12px'
                }}
                formatter={(value: any, name: string, props: any) => {
                  if (name === 'actual') {
                    const changePercent = ((props.payload.actual - baseline) / baseline * 100).toFixed(1);
                    return [
                      <div key="actual">
                        <div>{value} m</div>
                        <div className="text-xs text-[#F07D13]">Change: {changePercent}%</div>
                        <div className="text-xs text-[#494949]">{props.payload.date}</div>
                      </div>,
                      'Actual Distance'
                    ];
                  }
                  if (name === 'target') return [`${value} m`, 'Target'];
                  if (name === 'baseline') return [`${value} m`, 'Baseline'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Week ${label}`}
              />
              <ReferenceLine 
                y={baseline} 
                stroke="#F07D13" 
                strokeDasharray="5 5" 
                label={{ value: 'Baseline', position: 'insideTopRight', fontSize: 10, fill: '#F07D13' }} 
              />
              <ReferenceLine 
                y={target} 
                stroke="#14CAF4" 
                strokeDasharray="5 5" 
                label={{ value: 'Target', position: 'insideTopRight', fontSize: 10, fill: '#14CAF4' }} 
              />
              <Area
                type="monotone"
                dataKey="actual"
                stroke="#2762AC"
                strokeWidth={3}
                fill="url(#sixMWDGradient)"
                dot={{ fill: '#2762AC', r: 6, strokeWidth: 2, stroke: 'white' }}
                name="actual"
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-6 p-4 bg-[#F5F5F5] rounded-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-[#2762AC] rounded"></div>
            <span className="text-sm text-[#494949]">Actual Distance</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 border-t-2 border-dashed border-[#14CAF4]"></div>
            <span className="text-sm text-[#494949]">Target Distance</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 border-t-2 border-dashed border-[#F07D13]"></div>
            <span className="text-sm text-[#494949]">Baseline</span>
          </div>
        </div>

        {/* Progress Insights */}
        <div className="p-4 bg-gradient-to-r from-[#E5E5E3] to-[#F5F5F5] rounded-xl border border-[#E5E5E3]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm mb-2 text-[#494949]">Progress Analysis</p>
              <p className="text-[#494949] text-xs">
                Distance to Target: <span className="text-[#F07D13]">{target - currentData.actual} m</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#494949]">Current Performance</p>
              <p className="text-[#2762AC]">
                {((currentData.actual / target) * 100).toFixed(1)}% of goal
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}